/**
 * The result page's view of a graded paper for the exam wrapper: problems
 * joined from the session's questions and results, the sitting's pace, and the
 * cause state the page edits. Pure; the logic itself is `@shared/examWrapper`.
 */
import {
  EXAM_CAUSES,
  TODAY_EXTRAS_DAYS,
  type ExamCause,
  type Sitting,
  type WrapperProblem,
} from "@shared/examWrapper";
import type { AnswerEntry, GradeResultDto, ReflectionDto, SessionDto } from "../types";

/** The page's editable causes: question id → causes in `EXAM_CAUSES` order. */
export type CauseState = Record<string, ExamCause[]>;

/** One problem per result, in slot order, with its question's chapter and primary topic. */
export function wrapperProblems(
  session: SessionDto,
  results: readonly GradeResultDto[],
): WrapperProblem[] {
  const questions = new Map(session.questions.map((entry) => [entry.question.id, entry.question]));
  return results
    .map((result) => {
      const question = questions.get(result.questionId);
      return {
        questionId: result.questionId,
        slot: result.slot,
        score: result.score,
        maxScore: result.maxScore,
        chapter: question?.chapter ?? 0,
        topic: question?.topic ?? "",
      };
    })
    .sort((a, b) => a.slot - b.slot);
}

/** The sitting's length in minutes and the paper's total points, for the timed-pair pace. */
export function sittingOf(session: SessionDto, results: readonly GradeResultDto[]): Sitting {
  return {
    minutes: (session.deadlineAt - session.startedAt) / 60_000,
    paperPoints: results.reduce((sum, result) => sum + result.maxScore, 0),
  };
}

/**
 * Whether the saved reflection was added to Today's session within the last `TODAY_EXTRAS_DAYS`
 * days, i.e. whether Today still serves its suggestions (E7). After that the button comes back.
 */
export function addedToToday(
  reflection: Pick<ReflectionDto, "addedToTodayAt"> | null,
  now: number = Date.now(),
): boolean {
  const at = reflection?.addedToTodayAt;
  return typeof at === "number" && now - at < TODAY_EXTRAS_DAYS * 86_400_000;
}

/** Turns one cause on or off, keeping the fixed order and dropping a problem left without causes. */
export function toggleCause(
  causes: CauseState,
  questionId: string,
  cause: ExamCause,
  on: boolean,
): CauseState {
  const current = causes[questionId] ?? [];
  const next = EXAM_CAUSES.filter((c) => (c === cause ? on : current.includes(c)));
  return Object.fromEntries(
    Object.entries({ ...causes, [questionId]: next }).filter(([, list]) => list.length > 0),
  );
}

/** No final answer and no notes were saved for the problem. */
export function isBlankAnswer(answer: AnswerEntry | undefined): boolean {
  return !answer?.final?.trim() && !answer?.notes?.trim();
}
